import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Countermeasure,
  DesignChange,
  FailureMode,
  ConvertToVerificationRequest,
} from '../../../types';
import { VerificationSession } from '../../../types/verification';
import { convertToVerification } from '../../../services/drbfm.service';
import { listSessions, getSession } from '../../../services/verificationService';
import {
  convertToVerificationSchema,
  newVerificationUnitSchema,
} from '@/features/verification/schemas/convertToVerification.schema';
import { safeParseLocalized } from '@/lib/forms/messages';
import { generatePreviewItems } from './previewMapping';
import { resolveApiErrorMessage } from '../../../utils/apiErrorMessages';

interface UseConvertWizardArgs {
  open: boolean;
  onClose: () => void;
  drbfmSessionId: string;
  drbfmItemName: string;
  failureModes: FailureMode[];
  designChanges: DesignChange[];
  countermeasures: Countermeasure[];
}

const TARGET_STEP = 1;
const selectionKey = (ids: Set<string>) => Array.from(ids).sort().join('|');

export const useConvertWizard = ({
  open,
  onClose,
  drbfmSessionId,
  drbfmItemName,
  failureModes,
  designChanges,
  countermeasures,
}: UseConvertWizardArgs) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);

  // Step 1: Failure mode selection
  const [selectedFMIds, setSelectedFMIds] = useState<Set<string>>(new Set());

  // Step 2: Target session/unit
  const [targetMode, setTargetMode] = useState<'existing' | 'new'>('new');
  const [existingSessions, setExistingSessions] = useState<VerificationSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [selectedSession, setSelectedSession] = useState<VerificationSession | null>(null);
  const [newSessionTitle, setNewSessionTitle] = useState('');
  const [newModelName, setNewModelName] = useState('');
  const [newUnitNumber, setNewUnitNumber] = useState('01');
  const [newUnitName, setNewUnitName] = useState('');

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [resultSessionId, setResultSessionId] = useState<string | null>(null);
  const [resultItemsCount, setResultItemsCount] = useState(0);

  // Sessions loading
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const defaultSelectionRef = useRef('');

  // Initialize: select critical/high severity failure modes by default
  useEffect(() => {
    if (open) {
      const defaultSelected = new Set(
        failureModes
          .filter(
            (fm) => fm.severity === 'critical' || fm.severity === 'high',
          )
          .map((fm) => fm.id),
      );
      setSelectedFMIds(defaultSelected);
      defaultSelectionRef.current = selectionKey(defaultSelected);
      setActiveStep(0);
      setError(null);
      setSuccess(false);
      setTargetMode('new');
      setNewSessionTitle(t('drbfm.convert.defaultSessionTitle', { itemName: drbfmItemName }));
      setNewModelName('');
      setNewUnitNumber('01');
      setNewUnitName(drbfmItemName);
      setSelectedSessionId('');
      setSelectedUnitId('');
      setSelectedSession(null);
      setExistingSessions([]);
    }
    // Initialize on dialog open / new DRBFM identity only — NOT on language
    // change. t() supplies the initial localized default title; making it a
    // dependency would reset the in-progress wizard whenever the user toggles
    // language (PR #790 review).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, failureModes, drbfmItemName]);

  // Load existing verification sessions when switching to "existing" mode
  useEffect(() => {
    if (targetMode === 'existing' && existingSessions.length === 0) {
      setSessionsLoading(true);
      listSessions({ limit: 50, offset: 0 })
        .then((res) => {
          setExistingSessions(res.sessions);
        })
        .catch(() => {
          setError(t('drbfm.convert.fetchSessionsFailed'));
        })
        .finally(() => setSessionsLoading(false));
    }
    // t() is only referenced for the failure message; it must not retrigger the
    // fetch on language change (PR #790 review).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMode, existingSessions.length]);

  // Load session details when a session is selected. Guarded so a slow
  // response for a previously selected session cannot overwrite the current one.
  useEffect(() => {
    if (!selectedSessionId) {
      setSelectedSession(null);
      return;
    }
    let stale = false;
    getSession(selectedSessionId)
      .then((s) => { if (!stale) setSelectedSession(s); })
      .catch(() => { if (!stale) setSelectedSession(null); });
    return () => { stale = true; };
  }, [selectedSessionId]);

  // Sorted failure modes: critical > high > medium > low
  const sortedFMs = useMemo(() => {
    const order: Record<string, number> = {
      critical: 0,
      high: 1,
      medium: 2,
      low: 3,
    };
    return [...failureModes].sort(
      (a, b) => (order[a.severity] ?? 4) - (order[b.severity] ?? 4),
    );
  }, [failureModes]);

  // Selected failure modes for preview
  const selectedFMs = useMemo(
    () => sortedFMs.filter((fm) => selectedFMIds.has(fm.id)),
    [sortedFMs, selectedFMIds],
  );

  // Preview items
  const previewItems = useMemo(
    () => generatePreviewItems(selectedFMs, designChanges, countermeasures),
    [selectedFMs, designChanges, countermeasures],
  );

  // Handlers
  const handleToggleFM = useCallback((fmId: string) => {
    setSelectedFMIds((prev) => {
      const next = new Set(prev);
      if (next.has(fmId)) {
        next.delete(fmId);
      } else {
        next.add(fmId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedFMIds(new Set(failureModes.map((fm) => fm.id)));
  }, [failureModes]);

  const handleDeselectAll = useCallback(() => {
    setSelectedFMIds(new Set());
  }, []);

  // Nothing here is persisted until submit, so anything past the pre-filled
  // step-1 selection is lost on dismissal.
  const isDirty = !success && (
    activeStep > 0 || selectionKey(selectedFMIds) !== defaultSelectionRef.current
  );

  const canProceedStep1 = selectedFMIds.size > 0;
  const isCreatingUnit =
    targetMode === 'new' ||
    (targetMode === 'existing' && selectedSessionId.length > 0 && selectedUnitId.length === 0);
  const hasNewUnitDetails =
    newUnitNumber.trim().length > 0 && newUnitName.trim().length > 0;
  const canProceedStep2 =
    targetMode === 'new'
      ? newSessionTitle.trim().length > 0 && hasNewUnitDetails
      : selectedSessionId.length > 0 && (selectedUnitId.length > 0 || hasNewUnitDetails);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    const isNew = targetMode === 'new';
    let normalizedSession = {
      newSessionTitle,
      newSessionModelName: newModelName,
    };
    let normalizedUnit = { newUnitNumber, newUnitName };

    if (isNew) {
      const parsed = safeParseLocalized(convertToVerificationSchema, {
        ...normalizedSession,
        ...normalizedUnit,
      });
      if (!parsed.success) {
        // The offending inputs live on the target step; submit happens on the preview.
        setFieldErrors(Object.fromEntries(
          parsed.error.issues.map((issue) => [String(issue.path[0] ?? ''), issue.message]),
        ));
        setActiveStep(TARGET_STEP);
        return;
      }
      normalizedSession = {
        newSessionTitle: parsed.data.newSessionTitle,
        newSessionModelName: parsed.data.newSessionModelName,
      };
      normalizedUnit = {
        newUnitNumber: parsed.data.newUnitNumber,
        newUnitName: parsed.data.newUnitName,
      };
    } else if (isCreatingUnit) {
      const parsed = safeParseLocalized(newVerificationUnitSchema, normalizedUnit);
      if (!parsed.success) {
        setFieldErrors(Object.fromEntries(
          parsed.error.issues.map((issue) => [String(issue.path[0] ?? ''), issue.message]),
        ));
        setActiveStep(TARGET_STEP);
        return;
      }
      normalizedUnit = parsed.data;
    }

    setIsSubmitting(true);
    setFieldErrors({});
    setError(null);

    try {
      const request: ConvertToVerificationRequest = {
        failureModeIds: Array.from(selectedFMIds),
        verificationSessionId: isNew ? null : selectedSessionId,
        verificationUnitId: !isNew && selectedUnitId ? selectedUnitId : null,
        newSessionTitle: isNew ? normalizedSession.newSessionTitle : null,
        newSessionModelName: isNew ? normalizedSession.newSessionModelName : null,
        newUnitNumber: isCreatingUnit ? normalizedUnit.newUnitNumber : null,
        newUnitName: isCreatingUnit ? normalizedUnit.newUnitName : null,
      };

      const result = await convertToVerification(drbfmSessionId, request);
      setSuccess(true);
      setResultSessionId(result.verificationSessionId);
      setResultItemsCount(result.itemsCreated);
    } catch (err) {
      setError(resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.convert.convertFailed' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavigateToVerification = () => {
    onClose();
    if (resultSessionId) {
      navigate(`/verification?session=${resultSessionId}`);
    }
  };

  const handleSelectSession = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setSelectedUnitId('');
    // Do not leave the previous session's units selectable while the next
    // session is loading. Otherwise a fast selection can pair session B with
    // a unit that belongs to session A and produce a guaranteed 404.
    setSelectedSession(null);
  };

  return {
    activeStep,
    selectedFMIds,
    sortedFMs,
    previewItems,
    targetMode,
    setTargetMode,
    existingSessions,
    sessionsLoading,
    selectedSessionId,
    selectedUnitId,
    setSelectedUnitId,
    selectedSession,
    newSessionTitle,
    setNewSessionTitle,
    newModelName,
    setNewModelName,
    newUnitNumber,
    setNewUnitNumber,
    newUnitName,
    setNewUnitName,
    isSubmitting,
    error,
    setError,
    fieldErrors,
    success,
    resultItemsCount,
    isCreatingUnit,
    isDirty,
    canProceedStep1,
    canProceedStep2,
    handleToggleFM,
    handleSelectAll,
    handleDeselectAll,
    handleNext,
    handleBack,
    handleSubmit,
    handleNavigateToVerification,
    handleSelectSession,
  };
};

export type ConvertWizard = ReturnType<typeof useConvertWizard>;
